<template>
  <div class="canceled-settlements-table">
    <v-card-title class="toolbar primary white--text mb-0 pb-3">
      <div>
        <span class="headline">
          <h1 class="headline ml-2">
            <v-icon dark>description</v-icon>
            Canceled Settlements
          </h1>
        </span>
      </div>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter results"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
      />
    </v-card-title>
    <v-card-title class="toolbar primary white--textn pt-0">
      <v-select
        v-model="searchKey"
        :items="settlementSearchKeys"
        label="Search by ..."
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
        class="ml-2 mr-2"
      />
      <v-text-field
        v-if="isUserIdOrStatus(searchKey)"
        v-model="searchValue"
        append-icon="search"
        label="search value"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
        class="ml-2 mr-2"
      />
      <v-text-field
        v-else-if="searchKey === 'userId'"
        v-model.number="searchValue"
        type="number"
        append-icon="search"
        label="search value(numbers only)"
        single-line
        flat
        dark
        color="primary"
        class="ml-2 mr-2"
        solo-inverted
        hide-details
      />
      <v-select
        v-else
        v-model="searchValue"
        :items="settlementStatus"
        append-icon="search"
        label="Select status"
        single-line
        flat
        dark
        color="primary"
        class="ml-2 mr-2"
        solo-inverted
        hide-details
      />
      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            class="right-margin-double"
            color="secondary"
            fab
            dark
            @click.stop="() => downloadCsv()"
            v-on="on"
          >
            <v-icon>file_download</v-icon>
          </v-btn>
        </template>
        <span>Download CSV</span>
      </v-tooltip>
    </v-card-title>

    <!-- DATA TABLE -->
    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
    >
      <!-- CANCEL DATA TABLE ROW START -->
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          @click="toggleSelected(props.item)"
        >
          <td class="">
            {{ props.item.id }}
          </td>
          <td class="">
            {{ props.item.referenceSettlementId }}
          </td>
          <td class="">
            {{ props.item.status }}
          </td>
          <td class="">
            {{ props.item.eventType }}
          </td>
          <td>
            <v-btn
              style="text-transform: none;"
              outline
              round
              color="grey"
              :href="`/#/users/${props.item.data.userId}`"
            >
              <v-icon>
                assignment_ind
              </v-icon>
              {{ props.item.data.userId }}
            </v-btn>
          </td>
          <td>
            {{ props.item.data.strategyId }}
          </td>
          <td>
            {{ props.item.data.pieces }}
          </td>
          <td>
            {{ getWalletAddress(props.item) }}
          </td>
          <td>
            {{ props.item.securityId }}
          </td>
          <td class="text-xs">
            {{ (new Date(props.item.timestamp)).toISOString() }}
          </td>
          <td class="text-xs">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => onShowSettlement(props.item.id)"
                >
                  <v-icon>history</v-icon>
                </v-btn>
              </template>
              <span>History</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => onActionsSettlement(props.item)"
                >
                  <v-icon>build</v-icon>
                </v-btn>
              </template>
              <span>Actions</span>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="props.item.id && selected === props.item.id">
          <td colspan="6">
            <SettlementDetails
              :settlement="props.item"
            />
          </td>
        </tr>
      </template>
      <!-- CANCEL DATA TABLE ROW END -->
    </v-data-table>
    <ShowSettlementHistory
      v-if="isShowing"
      :is-open="isShowing"
      :toggle-open="toggleShowing"
      :settlement-id="showSettlementId"
      type="settlementCancel"
    />
    <ShowSettlementActions
      v-if="isActions"
      :is-open="isActions"
      :toggle-open="toggleActions"
      :settlement="actionsSettlement"
    />
  </div>
</template>

<script>


import ShowSettlementHistory from './dialog/ShowSettlementHistory';
import SettlementDetails from './shared/SettlementDetails';
import SettlementSearchKeysEnum from '../../enum/settlements/SettlementSearchKeys';
import SettlementStatusEnum from '../../enum/settlements/SettlementStatus';
import ShowSettlementActions from './dialog/ShowSettlementActions';
import { downloadFromS3 } from '@/helpers/axiosS3Downloader';
import { downloadCsvFile } from '@/helpers';

export default {
  name: 'CanceledSettlementsTable',
  components: {
    ShowSettlementHistory,
    SettlementDetails,
    ShowSettlementActions,
  },
  data() {
    return {
      showSettlementId: null,
      isLoading: false,
      isActions: false,
      isShowing: false,
      currEditSigning: {},
      selected: 0,
      pagination: {
        rowsPerPage: '40',
        sortBy: 'Updated At',
        descending: true,
      },
      search: '',
      searchKey: null,
      searchValue: '',
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Reference Settlement ID', value: 'referenceSettlementId' },
        { text: 'Status', value: 'status' },
        { text: 'Event Type', value: 'eventType' },
        { text: 'User ID', value: 'userId' },
        { text: 'Strategy ID', value: 'strategyId' },
        { text: 'Pieces', value: 'data.pieces' },
        { text: 'Vault Wallet Address', value: 'data.strategy.blockchainVault.walletAddress' },
        { text: 'Token Symbol', value: 'securityId' },
        { text: 'Updated At', value: 'timestamp' },
        { text: 'Actions', value: null, sortable: false },
      ],
    };
  },
  computed: {
    canceledSettlements() {
      return this.$store.state.canceledSettlementsStore.canceledSettlements;
    },
    settlementStatus() {
      return Object.keys(SettlementStatusEnum).map(key => SettlementStatusEnum[key]);
    },
    settlementSearchKeys() {
      return Object.keys(SettlementSearchKeysEnum).map(key => SettlementSearchKeysEnum[key]);
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.canceledSettlements :
        this.canceledSettlements.filter((item) => {
          let found = false;
          Object.keys(item)
            .forEach((key) => {
              if (
                item[key] && String(item[key]).indexOf(search) > -1
              ) {
                found = true;
              }

              if (String(item.data.strategy.blockchain.walletAddress).indexOf(search) > -1) {
                found = true;
              }

              if (String(item.data.userId).indexOf(search) > -1) {
                found = true;
              }
            });

          return found;
        });
    },
  },
  watch: {
    searchKey() {
      this.searchValue = '';
    },
    searchValue(val) {
      if (val !== '') {
        this.getCanceledSettlements(this.searchKey, val);
      }
    },
  },
  mounted() {
    this.getCanceledSettlements();
  },
  methods: {
    isUserIdOrStatus(key) {
      if (!['status', 'userId'].includes(key)) {
        return true;
      }

      return false;
    },
    getCanceledSettlements(key, value) {
      this.isLoading = true;
      this.$store.dispatch('fetchCanceledSettlements', { key, value }).finally(() => {
        this.isLoading = false;
      });
    },
    downloadCsv() {
      const key = this.searchKey;
      const value = this.searchValue;
      const type = 'cancelledSettlements';

      this.$store
        .dispatch('fetchStrategiesSettlementsDownloadCsv', { key, value, type })
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        });
    },
    toggleActions() {
      this.isActions = !this.isActions;
    },
    onActionsSettlement(showSettlement) {
      this.isActions = true;
      this.actionsSettlement = showSettlement;
    },
    getWalletAddress(item) {
      return item.data.strategy.blockchainVault ?
        item.data.strategy.blockchainVault.walletAddress :
        null;
    },
    onShowSettlement(showSettlementId) {
      this.isShowing = true;
      this.showSettlementId = showSettlementId;
    },
    toggleShowing() {
      this.isShowing = !this.isShowing;
    },
    toggleSelected(settlement) {
      this.selected = this.selected === settlement.id ? null : settlement.id;
    },
  },
};
</script>
<style lang="scss">
  .canceled-settlements-table {
    > .toolbar {
      position: relative;
      padding: 24px 24px 32px 24px;
      margin-bottom: 32px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/settlements/CanceledSettlementsTable.vue